import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { VariableSizeList as List } from 'react-window';
import { Modal, Button } from 'react-bootstrap';
import { i18n } from 'app/utils/i18n';
import { useSuggestTableTags, Linkrole, AiPrediction } from 'app/api/tag-queries/ai-tagging';
import { useTagsByDocument } from 'app/api/tag-queries/tags';
import { TagOrigin } from 'app/api/tag-queries/types';
import { useApplyCellTags } from './Footer/useApplyCellTags';
import { useEditorSelector } from 'app/state/contexts/EditorContext';
import { useDocumentTaxonomyConfig, useUpdateDocumentTaxonomyConfig } from 'app/api/tag-queries/document-taxonomy-config';
import { usePresentationTree } from 'app/api/tag-queries/taxonomy';
import { TagContext } from 'app/state/contexts/TagContext';
import { dispatchMessage, EventType } from 'app/useToasts';
import Spinner from 'app/components/Spinner';
import { StyledDataSheet } from 'app/components';
import { RawCell, cellText as rawCellText, isPureNumericText, isNoteColumn } from 'app/utils/tableCells';
import './AiApplicationModal.scss';
import { Typography } from 'app/components/ui';
import { LinkroleCell, linkroleLabel } from './LinkroleCell';
import { buildLinkroleTree, collectTreeTokens, prefillSelection, keepLinkroleForConcept, buildAncestorIndex, parseToken } from './aiLinkroleFilter';
import { LinkroleFilterTree } from './LinkroleFilterTree';
import { getTagLabel } from 'app/utils/tag-utils/getTagLabel';
import { AiResultTabs } from './AiResultTabs';
import {
  buildConceptOrderIndex,
  flattenLista,
  flattenRad,
  flattenKoncept,
  conceptGroupEligibleKeys,
  groupCheckState,
  cellKey,
  rowKey,
  ResultTab,
  GroupedRow,
  PredRow,
} from './aiResultGrouping';

interface AiApplicationModalProps {
  show: boolean;
  onClose: () => void;
  referenceDataSuperId: string;
  documentSuperId: number;
  tableTitle?: string;
}

const ROW_HEIGHT = 52;
const LIST_HEIGHT = Math.round((typeof window !== 'undefined' && window.innerHeight ? window.innerHeight : 800) * 0.66);
const MAX_AI_CELLS = 500; // matches the backend TOO_MANY_CELLS cap

export const AiApplicationModal = ({ show, onClose, referenceDataSuperId, documentSuperId, tableTitle }: AiApplicationModalProps) => {
  const { mutate: predict, data, isLoading, error, reset } = useSuggestTableTags();
  const { data: tagsData } = useTagsByDocument(documentSuperId, { include: ['concept', 'metadata', 'unit', 'context'] });
  const applyCellTags = useApplyCellTags();
  const { section, activeSection, locale, references } = useEditorSelector((editor: any) => editor);
  const { data: config } = useDocumentTaxonomyConfig(documentSuperId);
  const taxonomyId = config?.taxonomySelections?.find((s: any) => s.isActive)?.taxonomy?.id ?? null;
  const { tagLanguage } = useContext(TagContext) ?? {};
  const { data: presentationTree } = usePresentationTree(documentSuperId);
  const { mutate: updateConfig } = useUpdateDocumentTaxonomyConfig();

  const linkroleOverrides = useMemo<Record<string, { alias?: string; hidden?: boolean }>>(
    () => (config as any)?.taxonomyOverrides?.[taxonomyId ?? ''] ?? {},
    [config, taxonomyId]
  );

  const linkroleTree = useMemo(
    () => buildLinkroleTree(presentationTree, linkroleOverrides, tagLanguage ?? 'sv'),
    [presentationTree, linkroleOverrides, tagLanguage]
  );
  const linkroleTokenValues = useMemo(() => collectTreeTokens(linkroleTree), [linkroleTree]);
  const ancestorIndex = useMemo(() => buildAncestorIndex(presentationTree), [presentationTree]);
  const [selectedLinkroles, setSelectedLinkroles] = useState<string[]>([]);
  const selectedTokens = useMemo(() => selectedLinkroles.map(parseToken), [selectedLinkroles]);

  useEffect(() => {
    if (!show) return;
    setSelectedLinkroles(prefillSelection(config?.aiLinkroleFilters?.[referenceDataSuperId], linkroleTokenValues));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, config, referenceDataSuperId, linkroleTokenValues]);

  const toggleLinkrole = (uri: string) => setSelectedLinkroles((prev) => (prev.includes(uri) ? prev.filter((u) => u !== uri) : [...prev, uri]));

  const linkroleFilterControl = <LinkroleFilterTree forest={linkroleTree} selected={selectedLinkroles} onToggle={toggleLinkrole} />;

  const linkroleRows = (p: AiPrediction): Linkrole[] =>
    (p.linkroles ?? [])
      .filter((lr) => linkroleOverrides[lr.uri]?.hidden !== true)
      .filter((lr) => keepLinkroleForConcept(lr, p.conceptName, selectedTokens, ancestorIndex));

  const tagById = useMemo(() => {
    const m = new Map<string, { conceptId: string; origin: TagOrigin; linkroleUri: string | null }>();
    (tagsData?.tags ?? []).forEach((t: any) => m.set(t.id, { conceptId: t.conceptId, origin: t.origin ?? 'human', linkroleUri: t.linkroleUri ?? null }));
    return m;
  }, [tagsData]);

  const refData = useMemo(() => references?.find((r: any) => r.superId === referenceDataSuperId), [references, referenceDataSuperId]);
  const valueOf = (row: number, col: number): string => {
    const cell = refData?.selectionContent?.[row]?.[col];
    if (cell == null) return '';
    return typeof cell === 'string' ? cell : String(cell.value ?? '');
  };
  const appliedTag = (row: number, col: number, conceptId: string): { origin: TagOrigin; linkroleUri: string | null } | null => {
    const ids: string[] = refData?.selectionContent?.[row]?.[col]?.tags ?? [];
    for (const id of ids) {
      const t = tagById.get(id);
      if (t && t.conceptId === conceptId) return { origin: t.origin, linkroleUri: t.linkroleUri ?? null };
    }
    return null;
  };
  const appliedOrigin = (row: number, col: number, conceptId: string, linkroleUri: string): TagOrigin | null => {
    const t = appliedTag(row, col, conceptId);
    if (!t) return null;
    return t.linkroleUri == null || t.linkroleUri === linkroleUri ? t.origin : null;
  };
  const cellHasAnyTag = (row: number, col: number): boolean => (refData?.selectionContent?.[row]?.[col]?.tags ?? []).length > 0;
  const rowsForPrediction = (p: AiPrediction, row: number, col: number): Linkrole[] => {
    const rows = linkroleRows(p);
    const t = appliedTag(row, col, p.conceptId);
    return t && t.linkroleUri != null ? rows.filter((lr) => lr.uri === t.linkroleUri) : rows;
  };

  const grid: RawCell[][] = useMemo(() => refData?.selectionContent ?? [], [refData]);

  const [included, setIncluded] = useState<Set<string>>(new Set());

  const isThead = (r: number, c: number): boolean => (grid[r]?.[c] as any)?.format === 'thead';

  const rowTextOf = (row: number): string => rawCellText(grid[row]?.[0]);
  const colTextOf = (row: number, col: number): string => {
    const colParts: string[] = [];
    for (let r = 0; r < row; r++) {
      let c = col;
      while (c >= 1 && !rawCellText(grid[r]?.[c])) c--;
      if (c < 1) continue;
      if (r !== 0 && !isThead(r, c)) continue;
      colParts.push(rawCellText(grid[r]?.[c]));
    }
    return colParts.join(' ');
  };
  const textOf = (row: number, col: number): string => [rowTextOf(row), colTextOf(row, col)].filter(Boolean).join(' — ');
  const isIncludable = (r: number, c: number): boolean => r >= 1 && c >= 1 && !isThead(r, c) && isPureNumericText(rawCellText(grid[r]?.[c]));

  useEffect(() => {
    if (!show) return;
    reset();
    const next = new Set<string>();
    const noteCols = new Map<number, boolean>();
    for (let r = 1; r < grid.length; r++) {
      for (let c = 1; c < (grid[r]?.length ?? 0); c++) {
        if (!isIncludable(r, c)) continue;
        if (!noteCols.has(c)) noteCols.set(c, isNoteColumn(grid, c));
        if (!noteCols.get(c)) next.add(cellKey(r, c));
      }
    }
    setIncluded(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, refData]);

  const toggleCells = (targets: { r: number; c: number }[]) => {
    const includable = targets.filter(({ r, c }) => isIncludable(r, c));
    if (!includable.length) return;
    setIncluded((prev) => {
      const allIn = includable.every(({ r, c }) => prev.has(cellKey(r, c)));
      const next = new Set(prev);
      includable.forEach(({ r, c }) => (allIn ? next.delete(cellKey(r, c)) : next.add(cellKey(r, c))));
      return next;
    });
  };

  const headerSpan = (r: number, c: number): number[] => {
    const rowLen = grid[r]?.length ?? 0;
    const cols = [c];
    for (let c2 = c + 1; c2 < rowLen && rawCellText(grid[r]?.[c2]) === ''; c2++) cols.push(c2);
    return cols;
  };

  const allCellsInRow = (r: number) => Array.from({ length: grid[r]?.length ?? 0 }, (_, c) => ({ r, c }));
  const allCellsInCols = (cols: number[]) => cols.flatMap((c) => grid.map((_, r) => ({ r, c })));

  const handlePickClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const cellEl = (e.target as HTMLElement).closest?.('td, th') as HTMLTableCellElement | null;
    if (!cellEl) return;
    const rowEl = cellEl.parentElement as HTMLTableRowElement | null;
    if (!rowEl) return;
    const r = rowEl.rowIndex;
    const c = cellEl.cellIndex;
    if (r === 0 && c === 0) return;
    if (c === 0) return toggleCells(allCellsInRow(r)); // row label → toggle row
    if (r === 0 || isThead(r, c)) return toggleCells(allCellsInCols(headerSpan(r, c))); // header → toggle spanned column(s)
    toggleCells([{ r, c }]); // data cell → toggle itself
  };

  const sheetData = useMemo(
    () =>
      grid.map((row, r) =>
        (row ?? []).map((cell, c) => {
          const base = cell != null && typeof cell === 'object' ? cell : { value: cell ?? '' };
          const background = [(base as any).background, included.has(cellKey(r, c)) ? 'ai-pick-on' : null].filter(Boolean).join(' ');
          return { ...base, readOnly: true, background };
        })
      ),
    [grid, included]
  );

  const titleToSend = (tableTitle?.trim() || rawCellText(grid[0]?.[0])).slice(0, 200);
  const displayTitle = titleToSend || refData?.name || '';

  const requestCells = useMemo(() => {
    const cells: { row: number; col: number }[] = [];
    for (let r = 1; r < grid.length; r++) {
      for (let c = 1; c < (grid[r]?.length ?? 0); c++) {
        if (included.has(cellKey(r, c))) cells.push({ row: r, col: c });
      }
    }
    return cells;
  }, [grid, included]);
  const cappedLocally = requestCells.length > MAX_AI_CELLS;

  const persistLinkroleFilter = () => {
    if (!config?.id) return;
    const existing = config.aiLinkroleFilters?.[referenceDataSuperId] ?? [];
    const sameAsSaved = existing.length === selectedLinkroles.length && existing.every((u) => selectedLinkroles.includes(u));
    if (sameAsSaved) return;
    updateConfig({
      configId: config.id,
      silent: true,
      aiLinkroleFilters: { ...(config.aiLinkroleFilters ?? {}), [referenceDataSuperId]: selectedLinkroles },
    });
  };

  const runPredict = () => {
    if (!requestCells.length) return;
    persistLinkroleFilter();
    predict({ referenceDataSuperId, documentSuperId, cells: requestCells.slice(0, MAX_AI_CELLS), ...(titleToSend ? { title: titleToSend } : {}) });
  };

  const [checked, setChecked] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!data) return;
    const next = new Set<string>();
    data.suggestions.forEach((s) => {
      if (cellHasAnyTag(s.row, s.col)) return; // #4: don't auto-suggest a replacement for an already-tagged cell
      const candidates = s.predictions.filter((p) => rowsForPrediction(p, s.row, s.col).length > 0);
      if (!candidates.length) return;
      const top = candidates.reduce((a, b) => (b.f1ScoreTest > a.f1ScoreTest ? b : a));
      next.add(rowKey(s.row, s.col, top.conceptId, rowsForPrediction(top, s.row, s.col)[0].uri)); // first visible linkrole
    });
    setChecked(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, selectedTokens, ancestorIndex]);

  const toggle = (key: string) =>
    setChecked((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const markAll = () => {
    if (!data) return;
    const next = new Set<string>();
    data.suggestions.forEach((s) => {
      s.predictions.forEach((p) => {
        const rows = rowsForPrediction(p, s.row, s.col);
        if (!rows.length) return;
        if (appliedOrigin(s.row, s.col, p.conceptId, rows[0].uri) !== null) return;
        next.add(rowKey(s.row, s.col, p.conceptId, rows[0].uri));
      });
    });
    setChecked(next);
  };
  const unmarkAll = () => setChecked(new Set());

  const toggleConceptGroup = (members: PredRow[]) => {
    const eligible = conceptGroupEligibleKeys(members, checked);
    if (!eligible.length) return;
    const allOn = groupCheckState(eligible, checked) === 'all';
    setChecked((prev) => {
      const next = new Set(prev);
      eligible.forEach((k) => (allOn ? next.delete(k) : next.add(k)));
      return next;
    });
  };

  const selectedConceptCells = useMemo(() => {
    const map = new Map<string, { row: number; col: number; value?: string; conceptId: string; linkroleUri: string | null }>();
    (data?.suggestions ?? []).forEach((s) => {
      const value = valueOf(s.row, s.col);
      s.predictions.forEach((p) => {
        for (const lr of rowsForPrediction(p, s.row, s.col)) {
          if (appliedOrigin(s.row, s.col, p.conceptId, lr.uri) !== null) continue; // skip already-applied rows
          if (!checked.has(rowKey(s.row, s.col, p.conceptId, lr.uri))) continue;
          map.set(`${s.row}:${s.col}:${p.conceptId}:${lr.uri}`, { row: s.row, col: s.col, value, conceptId: p.conceptId, linkroleUri: lr.uri });
        }
      });
    });
    return Array.from(map.values());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, checked, tagById, refData, linkroleOverrides]);

  const checkedCount = selectedConceptCells.length;

  const totalSelectable = useMemo(() => {
    let n = 0;
    (data?.suggestions ?? []).forEach((s) =>
      s.predictions.forEach((p) => {
        const rows = rowsForPrediction(p, s.row, s.col);
        if (rows.length > 0 && appliedOrigin(s.row, s.col, p.conceptId, rows[0].uri) === null) n += 1;
      })
    );
    return n;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, tagById, refData, linkroleOverrides]);
  const allSelected = totalSelectable > 0 && checkedCount === totalSelectable;
  const selectAllRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = checkedCount > 0 && checkedCount < totalSelectable;
  }, [checkedCount, totalSelectable]);

  const selectAllCheckbox = (
    <input
      ref={selectAllRef}
      type="checkbox"
      aria-label={i18n('tag.ai.selectAll')}
      checked={allSelected}
      disabled={totalSelectable === 0}
      onChange={() => (allSelected ? unmarkAll() : markAll())}
    />
  );

  const handleApply = async () => {
    if (!data || !taxonomyId) return;

    const cells = selectedConceptCells;
    if (cells.length === 0) return;

    const sectionContext = { documentSuperId, sectionSuperId: section.superId, pageId: activeSection.uuid };
    const res = await applyCellTags({
      mode: 'create',
      cells,
      referenceDataSuperId,
      conceptId: cells[0].conceptId, // fallback; each cell carries its own conceptId
      type: 'nonFraction',
      taxonomyId,
      sectionContext,
      locale,
      origin: 'ai',
    });
    const created = res?.createdCount ?? 0;

    dispatchMessage(EventType.ADD_MESSAGE, { type: 'success', text: `${created} ${i18n('tag.ai.applied')}`, removeTime: 4000 });
    if (created < cells.length) {
      dispatchMessage(EventType.ADD_MESSAGE, { type: 'error', text: `${cells.length - created} ${i18n('tag.ai.applied.partial')}`, removeTime: 6000 });
    }
    onClose();
  };

  const cellGroups = useMemo(() => {
    return (data?.suggestions ?? []).map((s) => {
      const value = valueOf(s.row, s.col);
      const text = textOf(s.row, s.col);
      const preds: PredRow[] = [];
      s.predictions.forEach((p) => {
        const rows = rowsForPrediction(p, s.row, s.col);
        rows.forEach((lr) => {
          const origin = appliedOrigin(s.row, s.col, p.conceptId, lr.uri);
          const applied = origin !== null;
          preds.push({ kind: 'pred', key: rowKey(s.row, s.col, p.conceptId, lr.uri), row: s.row, col: s.col, value, text, p, lr, rows, applied, origin });
        });
      });
      return { row: s.row, col: s.col, cellKey: cellKey(s.row, s.col), value, text, rowText: rowTextOf(s.row), colText: colTextOf(s.row, s.col), preds };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, tagById, refData, linkroleOverrides, selectedLinkroles]);

  const conceptOrderIndex = useMemo(() => buildConceptOrderIndex(presentationTree), [presentationTree]);

  const [activeTab, setActiveTab] = useState<ResultTab>('rad');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const toggleGroup = (key: string) =>
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const [prevData, setPrevData] = useState(data);
  if (prevData !== data) {
    setPrevData(data);
    setExpandedGroups(new Set());
  }

  const displayRows = useMemo<GroupedRow[]>(() => {
    if (activeTab === 'lista') return flattenLista(cellGroups);
    if (activeTab === 'koncept') return flattenKoncept(cellGroups, conceptOrderIndex, expandedGroups);
    return flattenRad(cellGroups, expandedGroups);
  }, [activeTab, cellGroups, conceptOrderIndex, expandedGroups]);

  const filteredToEmpty = selectedLinkroles.length > 0 && cellGroups.length > 0 && !cellGroups.some((c) => c.preds.length > 0);

  const listRef = useRef<List>(null);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const sizeMap = useRef<Record<number, number>>({});
  const setSize = useCallback((index: number, size: number) => {
    if (size > 0 && sizeMap.current[index] !== size) {
      sizeMap.current[index] = size;
      listRef.current?.resetAfterIndex(index);
    }
  }, []);
  const getItemSize = (index: number) => sizeMap.current[index] ?? ROW_HEIGHT;

  useEffect(() => {
    sizeMap.current = {};
    setExpandedKey(null);
    listRef.current?.resetAfterIndex(0);
  }, [displayRows]);

  const descOf = (p: AiPrediction) =>
    (p.labels && getTagLabel({ languages: p.labels as any, currentLang: (tagLanguage ?? 'sv') as any })) || p.label || p.conceptName;

  const renderPredRow = (it: PredRow) => {
    const { p, lr, rows, key, applied, origin, value, text } = it;
    const winningSibling = rows.find((other: Linkrole) => other.uri !== lr.uri && checked.has(rowKey(it.row, it.col, p.conceptId, other.uri)));
    const siblingChecked = !!winningSibling;
    const disabled = applied || siblingChecked;
    const isExpanded = expandedKey === key;
    const descLabel = descOf(p);
    const linkroleText = linkroleLabel(lr, { tagLanguage, alias: linkroleOverrides[lr.uri]?.alias });
    const originText = i18n(`tag.ai.origin.${applied ? origin : 'ai'}`);
    const toggleExpand = () => setExpandedKey((cur) => (cur === key ? null : key));
    const indented = activeTab !== 'lista';
    return (
      <>
        <div className={`ai-modal__rowmain${indented ? ' ai-modal__rowmain--member' : ''}`} onClick={toggleExpand}>
          <div role="cell" className="ai-modal__col-check" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={applied || checked.has(key)}
              disabled={disabled}
              onChange={() => toggle(key)}
              aria-label={`${p.conceptName} ${lr.uri}`}
            />
          </div>
          <div role="cell" className="ai-modal__col-text" title={text}>
            {text}
          </div>
          <div role="cell" className="ai-modal__col-value">
            {value}
          </div>
          <div role="cell" className="ai-modal__col-desc">
            {descLabel}
          </div>
          <div role="cell" className="ai-modal__col-linkrole" title={linkroleText}>
            <LinkroleCell linkrole={lr} tagLanguage={tagLanguage} alias={linkroleOverrides[lr.uri]?.alias} />
            {winningSibling ? (
              <div className="ai-modal__conflict-hint">
                {i18n('tag.ai.conflict.linkrole')}: {linkroleLabel(winningSibling, { tagLanguage, alias: linkroleOverrides[winningSibling.uri]?.alias })}
              </div>
            ) : null}
          </div>
          <div role="cell" className="ai-modal__col-concept">
            {p.conceptName}
          </div>
          <div role="cell" className="ai-modal__col-f1">
            {(p.f1ScoreTest * 100).toFixed(1)} %
          </div>
          <div role="cell" className="ai-modal__col-origin">
            <span className={`ai-origin-pill ai-origin-pill--${applied ? origin : 'ai'}`}>{originText}</span>
          </div>
          <div role="cell" className="ai-modal__col-mand">
            {p.mandatory ? <i className="fa-solid fa-check" aria-label="mandatory" /> : null}
          </div>
          <div role="cell" className="ai-modal__col-chevron" aria-hidden>
            <i className={`fa-solid fa-chevron-${isExpanded ? 'up' : 'down'}`} />
          </div>
        </div>
        {isExpanded && (
          <div className="ai-modal__detail">
            {[
              [i18n('tag.ai.col.value'), value],
              [i18n('tag.ai.col.text'), text],
              [i18n('tag.ai.col.description'), descLabel],
              [i18n('tag.ai.col.linkrole'), linkroleText],
              [i18n('tag.ai.col.concept'), p.conceptName],
              [i18n('tag.ai.col.f1'), `${(p.f1ScoreTest * 100).toFixed(1)} %`],
              [i18n('tag.ai.col.origin'), originText],
              [i18n('tag.ai.col.mandatory'), p.mandatory ? '✓' : '—'],
            ].map(([label, val]) => (
              <div className="ai-modal__detail-row" key={label}>
                <span className="ai-modal__detail-label">{label}</span>
                <span className="ai-modal__detail-value">{val}</span>
              </div>
            ))}
          </div>
        )}
      </>
    );
  };

  const VirtualRow = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    const it = displayRows[index];
    const rowRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
      if (rowRef.current) setSize(index, rowRef.current.getBoundingClientRect().height);
    });
    if (!it) return null;

    if (it.kind === 'empty') {
      return (
        <div role="row" style={style} className={`ai-modal__grid-row${it.muted ? ' ai-modal__grid-row--muted' : ''}`}>
          <div ref={rowRef}>
            <div className="ai-modal__rowmain" style={{ cursor: 'default' }}>
              <div role="cell" className="ai-modal__col-check" />
              <div role="cell" className="ai-modal__col-value">
                {it.value}
              </div>
              <div role="cell" className="ai-modal__col-text" title={it.text}>
                {it.text}
              </div>
              <div role="cell" className="ai-modal__empty">
                {i18n('tag.ai.no.suggestions')}
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (it.kind === 'cell-group') {
      const top = it.top;
      return (
        <div role="row" style={style} className="ai-modal__grid-row ai-modal__group-row">
          <div ref={rowRef}>
            <div className="ai-modal__ghead" onClick={() => toggleGroup(it.key)}>
              <div className="ai-modal__gcol-check" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={!!top && checked.has(top.key)}
                  disabled={!top}
                  onChange={() => top && toggle(top.key)}
                  aria-label={`${it.text} ${it.value}`}
                />
              </div>
              <div className="ai-modal__gcol-chevron" aria-hidden>
                <i className={`fa-solid fa-chevron-${it.expanded ? 'down' : 'right'}`} />
              </div>
              <div className="ai-modal__gcol-a" title={it.rowText}>
                {it.rowText}
              </div>
              <div className="ai-modal__gcol-b" title={it.colText}>
                {it.colText}
              </div>
              <div className="ai-modal__gcol-c">{it.value}</div>
              <div className="ai-modal__gcol-badge">{it.candidateCount > 1 ? <span className="ai-modal__count-badge">{it.candidateCount}</span> : null}</div>
            </div>
          </div>
        </div>
      );
    }

    if (it.kind === 'concept-group') {
      const eligible = conceptGroupEligibleKeys(it.members, checked);
      const state = groupCheckState(eligible, checked);
      const lrText = linkroleLabel(it.lr, { tagLanguage, alias: linkroleOverrides[it.lr.uri]?.alias });
      const countTitle = `${it.cellCount} ${i18n('tag.ai.group.cells')}${it.appliedCount > 0 ? ` · ${it.appliedCount} ${i18n('tag.ai.group.applied')}` : ''}`;
      return (
        <div role="row" style={style} className="ai-modal__grid-row ai-modal__group-row">
          <div ref={rowRef}>
            <div className="ai-modal__ghead" onClick={() => toggleGroup(it.key)}>
              <div className="ai-modal__gcol-check" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  ref={(el) => {
                    if (el) el.indeterminate = state === 'some';
                  }}
                  checked={state === 'all'}
                  disabled={eligible.length === 0}
                  onChange={() => toggleConceptGroup(it.members)}
                  aria-label={`${it.conceptName} ${it.lr.uri}`}
                />
              </div>
              <div className="ai-modal__gcol-chevron" aria-hidden>
                <i className={`fa-solid fa-chevron-${it.expanded ? 'down' : 'right'}`} />
              </div>
              <div className="ai-modal__gcol-a" title={it.descLabel}>
                {it.descLabel}
              </div>
              <div className="ai-modal__gcol-b" title={it.conceptName}>
                {it.conceptName}
              </div>
              <div className="ai-modal__gcol-c" title={lrText}>
                <LinkroleCell linkrole={it.lr} tagLanguage={tagLanguage} alias={linkroleOverrides[it.lr.uri]?.alias} />
              </div>
              <div className="ai-modal__gcol-badge">
                {it.expanded ? (
                  <span className="ai-modal__count-badge" title={countTitle}>
                    {it.cellCount}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (it.kind === 'leaf-header') {
      return (
        <div role="row" style={style} className="ai-modal__grid-row ai-modal__subhead-row">
          <div ref={rowRef}>
            <div className="ai-modal__subhead">
              <div role="columnheader" className="ai-modal__col-check" />
              <div role="columnheader" className="ai-modal__col-text">
                {i18n('tag.ai.col.text')}
              </div>
              <div role="columnheader" className="ai-modal__col-value">
                {i18n('tag.ai.col.value')}
              </div>
              <div role="columnheader" className="ai-modal__col-desc">
                {i18n('tag.ai.col.description')}
              </div>
              <div role="columnheader" className="ai-modal__col-linkrole">
                {i18n('tag.ai.col.linkrole')}
              </div>
              <div role="columnheader" className="ai-modal__col-concept">
                {i18n('tag.ai.col.concept')}
              </div>
              <div role="columnheader" className="ai-modal__col-f1">
                {i18n('tag.ai.col.f1')}
              </div>
              <div role="columnheader" className="ai-modal__col-origin">
                {i18n('tag.ai.col.origin')}
              </div>
              <div role="columnheader" className="ai-modal__col-mand">
                {i18n('tag.ai.col.mandatory')}
              </div>
              <div role="columnheader" className="ai-modal__col-chevron" aria-hidden />
            </div>
          </div>
        </div>
      );
    }

    const applied = it.applied;
    const siblingChecked = it.rows.some((other: Linkrole) => other.uri !== it.lr.uri && checked.has(rowKey(it.row, it.col, it.p.conceptId, other.uri)));
    const className = ['ai-modal__grid-row', applied ? 'ai-modal__row--applied' : '', siblingChecked ? 'ai-modal__row--excluded' : '']
      .filter(Boolean)
      .join(' ');
    return (
      <div role="row" style={style} className={className}>
        <div ref={rowRef}>{renderPredRow(it)}</div>
      </div>
    );
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, height: '100%' }}>
          <Spinner />
          <div style={{ fontSize: 20, textAlign: 'center' }}>{i18n('tag.ai.loading')}</div>
        </div>
      );
    }
    if (error) {
      const code = (error as any)?.response?.data?.code;
      return (
        <div className="ai-modal__status">
          {code === 'AI_WARMING' ? i18n('tag.ai.warming') : i18n('tag.ai.error')}
          <Button variant="link" onClick={runPredict}>
            {i18n('tag.ai.retry')}
          </Button>
        </div>
      );
    }
    if (!data) {
      return (
        <>
          <div className="ai-modal__pick-title">
            {i18n('tag.ai.pick.tabletitle')}: <strong>{displayTitle || '—'}</strong>
          </div>
          <div className="ai-modal__pick-info">
            {i18n('tag.ai.pick.instructions')} <strong>{requestCells.length}</strong> {i18n('tag.ai.pick.count')}
          </div>
          {cappedLocally ? (
            <div className="ai-modal__truncated" role="alert">
              <i className="fa-solid fa-triangle-exclamation" /> {i18n('tag.ai.truncated')}
            </div>
          ) : null}
          {linkroleFilterControl}
          <div className="ai-modal__picker" onClick={handlePickClick}>
            <StyledDataSheet data={sheetData} withTags={false} selected={null} onSelect={() => {}} />
          </div>
        </>
      );
    }

    if (filteredToEmpty) {
      return (
        <>
          {linkroleFilterControl}
          <div className="ai-modal__status" role="status">
            {i18n('tag.ai.results.noneMatch')}
          </div>
        </>
      );
    }

    return (
      <>
        {data.truncated || cappedLocally ? (
          <div className="ai-modal__truncated" role="alert">
            <i className="fa-solid fa-triangle-exclamation" /> {i18n('tag.ai.truncated')}
          </div>
        ) : null}
        {linkroleFilterControl}
        <AiResultTabs activeTab={activeTab} onTabChange={setActiveTab} />
        <div className="ai-modal__grid-scroll">
          <div className="ai-modal__grid" role="table">
            {activeTab === 'lista' ? (
              <div className="ai-modal__grid-head" role="row">
                <div role="columnheader" className="ai-modal__col-check">
                  {selectAllCheckbox}
                </div>
                <div role="columnheader" className="ai-modal__col-text">
                  {i18n('tag.ai.col.text')}
                </div>
                <div role="columnheader" className="ai-modal__col-value">
                  {i18n('tag.ai.col.value')}
                </div>
                <div role="columnheader" className="ai-modal__col-desc">
                  {i18n('tag.ai.col.description')}
                </div>
                <div role="columnheader" className="ai-modal__col-linkrole">
                  {i18n('tag.ai.col.linkrole')}
                </div>
                <div role="columnheader" className="ai-modal__col-concept">
                  {i18n('tag.ai.col.concept')}
                </div>
                <div role="columnheader" className="ai-modal__col-f1">
                  {i18n('tag.ai.col.f1')}
                </div>
                <div role="columnheader" className="ai-modal__col-origin">
                  {i18n('tag.ai.col.origin')}
                </div>
                <div role="columnheader" className="ai-modal__col-mand">
                  {i18n('tag.ai.col.mandatory')}
                </div>
                <div role="columnheader" className="ai-modal__col-chevron" aria-hidden />
              </div>
            ) : (
              <div className="ai-modal__grid-head" role="row">
                <div role="columnheader" className="ai-modal__gcol-check">
                  {selectAllCheckbox}
                </div>
                <div role="columnheader" className="ai-modal__gcol-chevron" aria-hidden />
                <div role="columnheader" className="ai-modal__gcol-a">
                  {activeTab === 'rad' ? i18n('tag.ai.col.row') : i18n('tag.ai.col.description')}
                </div>
                <div role="columnheader" className="ai-modal__gcol-b">
                  {activeTab === 'rad' ? i18n('tag.ai.col.column') : i18n('tag.ai.col.concept')}
                </div>
                <div role="columnheader" className="ai-modal__gcol-c">
                  {activeTab === 'rad' ? i18n('tag.ai.col.value') : i18n('tag.ai.col.linkrole')}
                </div>
                <div className="ai-modal__gcol-badge" aria-hidden />
              </div>
            )}
            <List
              ref={listRef}
              className="ai-modal__list"
              height={LIST_HEIGHT}
              width="100%"
              itemCount={displayRows.length}
              itemSize={getItemSize}
              overscanCount={6}
            >
              {VirtualRow}
            </List>
          </div>
        </div>
      </>
    );
  };

  return (
    <Modal show={show} onHide={onClose} animation={false} dialogClassName="ai-modal-dialog">
      <Modal.Header closeButton>
        <Modal.Title>
          <Typography variant="body-8" fontWeight="semibold">
            {i18n('tag.ai.modal.title')}
          </Typography>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>{renderBody()}</Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          {i18n('tag.ai.cancel')}
        </Button>
        {data ? (
          <>
            <Button variant="secondary" onClick={() => reset()}>
              {i18n('tag.ai.back')}
            </Button>
            <Button variant="primary" disabled={checkedCount === 0 || !taxonomyId} onClick={handleApply}>
              {i18n('tag.ai.apply')}
            </Button>
          </>
        ) : (
          <Button variant="primary" disabled={requestCells.length === 0 || isLoading} onClick={runPredict}>
            {i18n('tag.ai.fetch')}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
